<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, onServerPrefetch, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ClientOnly from '@/components/SSR/ClientOnly.vue'
import { useI18n } from 'vue-i18n'
import xss from 'xss'
import { injectHead } from '@unhead/vue'
import { useLocalizedRouter } from '@/composables/localizedRouter'
import { formatPrice } from '@/utils/helpers'
import { useEstablishmentHttp } from '@/http/establishmentHttp'
import JsonLd from '@/components/partials/JsonLd.vue'
import CartDesktop from '@/components/Cart/redesign/CartDesktop.vue'
import EstablishmentCategory
  from '@/pages/establishment/components/redesign/EstablishmentCategory/EstablishmentCategory.vue'
import LoadingEstablishmentCategory
  from '@/pages/establishment/components/redesign/EstablishmentCategory/LoadingEstablishmentCategory.vue'
import EstablishmentFAQ from './components/EstablishmentFAQ/EstablishmentFAQ.vue'
import Icon from '@/components/partials/Icon.vue'
import Button from '@/components/partials/Buttons/Button/Button.vue'
import EstablishmentHeader from './components/redesign/EstablishmentHeader/EstablishmentHeader.vue'
import EstablishmentHeaderLoading from './components/redesign/EstablishmentHeader/EstablishmentHeaderLoading.vue'
import EstablishmentToolbarTop from './components/redesign/EstablishmentToolbarTop/EstablishmentToolbarTop.vue'
import EstablishmentDiscounts from './components/EstablishmentDiscounts.vue'
import EstablishmentDiscountsLoading from './components/EstablishmentDiscountsLoading.vue'
import FoodModal from '@/pages/establishment/components/Modals/FoodModal/FoodModal.vue'
import InfoModal from '@/pages/establishment/components/Modals/InfoModal/InfoModal.vue'
import AddReviewModal from '@/pages/establishment/components/Modals/AddReviewModal/AddReviewModal.vue'
import RemarkModal from '../checkout/components/redesign/RemarkModal.vue'
import Toolbar from '@/components/Navigation/Toolbar.vue'
import Warning from '@/components/partials/Warning.vue'
import FreeDeliveryNoticeDrawer
  from '@/components/Cart/FreeDeliveryNotice/FreeDeliveryNoticeDrawer/FreeDeliveryNoticeDrawer.vue'
import Container from '@/components/Structure/Container/Container.vue'
import DefaultLayout from '@/layouts/default.vue'
// import { useGa } from '@/composables/useGa'
import type { Location } from '@/types/Location'
import { useCitiesHttp } from '@/http/citiesHttp'
import LocationModal from '../checkout/components/LocationModal.vue'
import type { BasketProduct } from '@/types/http/BasketProduct'
import { useBasket } from '@/composables/basket'
import { getCountry } from '@/router/utils/getCountry'
import { useModalStore } from '@/stores/useModalStore'
import { useSessionStore } from '@/stores/useSessionStore'
import { useCartStore } from '@/stores/useCartStore'
import { useMarketplaceStore } from '@/stores/ssr/useMarketplaceStore'
import { useTracking } from '@/composables/useTracking'
import createStructuredData from '@/pages/establishment/helpers/create-establishment-structured-data'
import { useBreadcrumbStore } from '@/stores/useBreadcrumbStore'
import { useEstablishmentStore } from '@/stores/ssr/useEstablishmentStore'
import { EstablishmentMode } from '@/enums/EstablishmentMode'
import { Distribution } from '@/enums/Distribution'
import { useSafeSSRContext } from '@/utils/useSafeSSRContext'
import { useCookies } from '@/composables/useCookies'
import { storeToRefs } from "pinia"

const {t} = useI18n()
const route = useRoute()
const router = useRouter()
const establishmentHttp = useEstablishmentHttp()
const {localizedRoute} = useLocalizedRouter()
const {show, reset} = useModalStore()
const {setBreadcrumbs} = useBreadcrumbStore()
const sessionStore = useSessionStore()
const cartStore = useCartStore()
const marketplaceStore = useMarketplaceStore()
const establishmentStore = useEstablishmentStore()
const tracking = useTracking()
const {emptyBasket} = useBasket()

const cityHttp = useCitiesHttp()
const head = injectHead()
const {getCookie} = useCookies()

const structuredData = ref()

const search = ref<string>('')

const state = reactive<{
  remarkProduct: BasketProduct | null
  showRemarkModal: boolean
  showLocationModal: boolean
  locationModalError: string | null
  changeDistributionTo: Distribution | null
}>({
  remarkProduct: null,
  showRemarkModal: false,
  showLocationModal: false,
  locationModalError: null,
  changeDistributionTo: null,
})

// const ga = useGa()

// Stores are used for hydration from ssr.
const {
  establishment,
  establishmentDistribution,
  discounts: establishmentDiscounts,
  faqs: establishmentFaqs,
} = storeToRefs(establishmentStore)
const { basketInfo } = storeToRefs(cartStore)
const { distribution, location } = storeToRefs(sessionStore)
const hasItemsFromEstablishment = computed<boolean>(() => cartStore.hasCart && cartStore.establishment !== null && cartStore.establishment.id === establishment.value?.id)
const totalPayable = computed<string>(() => formatPrice(basketInfo.value?.costs?.subtotal ?? 0))
const noLocationButShould = computed<boolean>(() => distribution.value === Distribution.Delivery && location.value === null)

const filteredProducts = computed(() => (establishmentDistribution.value?.menu.product_sets ?? []).flatMap(productSet => {
  const filteredProductsInSet = productSet.products.filter(product =>
      product.title.toLowerCase().includes(search.value.toLowerCase()),
  )

  return filteredProductsInSet.length > 0
      ? [{...productSet, products: filteredProductsInSet}]
      : []
}))

const seoContent = computed<string | null>(() => {
  if (establishment.value?.content.content) {
    return xss(establishment.value.content.content)
  }

  return null
})

const updateSearch = (text: string) => {
  search.value = text
}

onServerPrefetch(async () => {
  const {redirect} = useSafeSSRContext()

  try {
    await initEstablishment()

    if (null === establishment.value) {
      throw new Error('could not load establishment')
    }

    structuredData.value = createStructuredData(establishment.value, establishmentDistribution.value)

    let categories = establishment.value.categories

    // delivery methods string
    const deliveryMethods = []
    if (establishmentSupportDistribution(Distribution.TakeAway)) {
      deliveryMethods.push(t('seo.distribution_takeaway'))
    }

    if (establishmentSupportDistribution(Distribution.Delivery)) {
      deliveryMethods.push(t('seo.distribution_delivery'))
    }

    const deliveryString = deliveryMethods.length > 1
        ? t('seo.distribution_multiple', {
          method1: deliveryMethods[0],
          method2: deliveryMethods[1],
        })
        : deliveryMethods[0]

    const seo = {
      categoriesFirst:
          categories.length === 1
              ? categories[0].name
              : categories
                  .slice(0, Math.min(2, categories.length - 1))
                  .map((cat) => cat.name)
                  .join(', '),
      categoriesLast:
          categories.length >= 2 ? categories[categories.length - 1].name : '',
      distribution: deliveryString,
    }

    let description = t('seo.description', {
      establishment: establishment.value.title,
      street: establishment.value.location.street,
      city: establishment.value.location.city.name,
      categories: seo.categoriesLast
          ? t('seo.categories_or', {
            categoriesFirst: seo.categoriesFirst,
            categoriesLast: seo.categoriesLast,
          })
          : seo.categoriesFirst,
      distribution: seo.distribution,
    })

    if (establishment.value.content.meta_description) {
      description = xss(establishment.value.content.meta_description)
    }

    let title = t('seo.title', {
      establishment: establishment.value.title,
      categories: seo.categoriesFirst + (seo.categoriesLast ? `, ${seo.categoriesLast}` : ''),
    })

    if (establishment.value.content.meta_title) {
      title = establishment.value.content.meta_title
    }

    const image = {
      path: `https://res.cloudinary.com/dk91ryeja/image/upload/co_rgb:FF5F59,g_center,l_text:Ubuntu_52_bold_center:${encodeURIComponent(
          establishment.value.title,
      )},x_350,y_-15,w_450,c_fit/v1661155365/merchant-previewimage-1200x630_mclber.jpg`,
      width: 1200,
      height: 627,
      type: 'png',
    }

    head?.push({
      title,
      meta: [
        {
          property: 'og:title',
          content: title,
        },
        {
          property: 'twitter:title',
          content: title,
        },
        {
          hid: 'og:image',
          property: 'og:image',
          content: image.path,
        },
        {
          hid: 'og:image:type',
          property: 'og:image:type',
          content: image.type,
        },
        {
          hid: 'og:image:width',
          property: 'og:image:width',
          content: image.width,
        },
        {
          hid: 'og:image:height',
          property: 'og:image:height',
          content: image.height,
        },
        {
          name: 'description',
          content: description,
        },
        {
          property: 'og:description',
          content: description,
        },
        {
          property: 'twitter:description',
          content: description,
        },
        ...((import.meta.env.VITE_BUILD_ENV !== 'production' || establishment.value.mode !== EstablishmentMode.Live) ? [
          {
            name: 'robots',
            content: 'noindex',
          },
        ] : []),
      ],
    })
  } catch (e) {
    // if the establishment doesnt exist, try to navigate
    // to the city page, if that doesnt exist, navigate to the homepage
    try {
      await cityHttp.city(getCountry().value, String(route.params.city))

      redirect(`/${route.params.city}`)
    } catch (e) {
      redirect('/')
    }
  }
})

onMounted(async () => {
  await initEstablishment()

  // ga.trackViewEstablishment(establishment.value)

  if (establishment.value === null) {
    await router.push(localizedRoute({ name: 'marketplace' }))

    return
  }

  if (!establishmentSupportDistribution(distribution.value)) {
    const availableDistributions = establishment.value?.distributions.filter(
        (type: Distribution) => type !== Distribution.OnLocation,
    )

    if (availableDistributions.length > 0) {
      sessionStore.setDistribution(availableDistributions)
    }

    show({
      closable: true,
      title: t('errors.only.distribution.title'),
      message: t(`errors.only.distribution.${availableDistributions}`, {
        establishment: establishment.value.title,
      }),
      callbacks: [
        {
          label: t('buttons.continue'),
        },
      ],
    })
  }

  if (cartStore.basketInfo !== null) {
    const distType = cartStore.distribution
    const slug = cartStore.basketInfo.establishment.slug
    const country = cartStore.basketInfo.establishment.city.country.code.toLowerCase()
    const city = cartStore.basketInfo.establishment.city.slug

    const [ establishment, distributionInfo ] = await Promise.all([
      establishmentHttp.establishment(
          slug,
          country,
          city,
          null,
      ),
      establishmentHttp.establishmentByDistributionType(
          slug,
          country,
          city,
          distType,
          location.value,
      ),
    ])

    cartStore.setEstablishment(establishment)
    cartStore.setEstablishmentDistribution(distributionInfo)
  }

  const { utm_source, utm_medium } = route.query // @todo: tracking

  if (utm_medium || utm_source) {
    sessionStore.setTracking({ utm_source, utm_medium })
  }
})

async function hydrateEstablishmentStore(country: string, city: string, slug: string, hash: string | null): Promise<void> {
  // Load the establishment
  await hydrateEstablishment(country, city, slug, hash)

  decideDistribution()

  // zet distribution
  await Promise.all([
    hydrateEstablishmentDistribution(country, city, slug),
    hydrateDiscounts(country, city, slug),
    hydrateFaqs(country, city, slug),
  ])
}

async function hydrateEstablishment(country: string, city: string, slug: string, hash: string | null): Promise<void> {
  establishmentStore.setEstablishment(await establishmentHttp.establishment(
      slug,
      country,
      city,
      hash,
  ))
}

async function hydrateEstablishmentDistribution(country: string, city: string, slug: string): Promise<void> {
  establishmentStore.setEstablishmentDistribution(await establishmentHttp.establishmentByDistributionType(
      slug,
      country,
      city,
      distribution.value,
      location.value,
  ))
}

const getPreferredDistribution = (): Distribution | null => {
  // Create a mapping for possible distribution values
  const distributionMap: Record<`${Distribution}`, Distribution> = {
    [`${Distribution.TakeAway}`]: Distribution.TakeAway,
    [`${Distribution.Delivery}`]: Distribution.Delivery,
    [`${Distribution.OnLocation}`]: Distribution.OnLocation,
  }

  const isValidDistribution = (value: any): value is `${Distribution}` => value in distributionMap

  // First, check query string for valid distribution
  if (typeof route.query.distribution === 'string' && isValidDistribution(route.query.distribution)) {
    return distributionMap[route.query.distribution]
  }

  // If not found in query, check the cookie value
  const cookieDistribution = getCookie('bist_distribution')

  if (cookieDistribution && isValidDistribution(cookieDistribution)) {
    return distributionMap[cookieDistribution]
  }

  return null
}

const establishmentSupportDistribution = (distribution: Distribution): boolean => {
  return establishment.value.distributions.includes(distribution)
}

const decideDistribution = (): void => {
  const preferredDistribution = getPreferredDistribution()

  // Check if request has onLocation
  const requestedOnLocation = Object.keys(route.params).includes('onLocationId') && String(route.params.onLocationId) !== ''

  // If request requests onLocation
  if (requestedOnLocation) {
    // Onlocation not supported by establishment
    if (!establishmentSupportDistribution(Distribution.OnLocation)) {
      doesNotSupportOnLocationOrdersModal()

      return
    }

    // Onlocation is supported
    sessionStore.setDistribution(Distribution.OnLocation)

    sessionStore.setOnLocationIdentifier(String(route.params.onLocationId))

    return
  }

  if (preferredDistribution && establishmentSupportDistribution(preferredDistribution)) {
    sessionStore.setDistribution(preferredDistribution)

    return
  }

  // Request did not request onLocation and the user does not have a preferred dist
  sessionStore.setOnLocationIdentifier(null)

  // Default to delivery distribution
  if (establishmentSupportDistribution(Distribution.Delivery)) {
    sessionStore.setDistribution(Distribution.Delivery)
    return
  }

  // Fallback to takeaway distribution
  if (establishmentSupportDistribution(Distribution.TakeAway)) {
    sessionStore.setDistribution(Distribution.TakeAway)
    return
  }

  throw new Error('Could not determine distribution for establishment')
}

async function hydrateDiscounts(country: string, city: string, slug: string): Promise<void> {
  establishmentStore.setDiscounts(await establishmentHttp.discounts(
      slug,
      country,
      city,
  ))
}

async function hydrateFaqs(country: string, city: string, slug: string): Promise<void> {
  establishmentStore.setFaqs(await establishmentHttp.faq(
      slug,
      country,
      city,
  ))
}

const initEstablishment = async () => {
  await hydrateEstablishmentStore(
      route.params.country ? String(route.params.country) : getCountry().value,
      String(route.params.city),
      String(route.params.slug),
      route.query.hash ? String(route.query.hash) : null,
  )

  if (establishment.value) {
    setBreadcrumbs([
      {
        name: establishment.value.location.city.name,
        route: localizedRoute({
          name: 'slug',
          params: {
            slug: establishment.value.location.city.slug,
          },
        }),
      },
      {
        route: localizedRoute({
          name: 'establishment',
          params: { city: establishment.value.location.city.slug, slug: establishment.value.slug },
        }),
        name: establishment.value.title,
      },
    ])
  }
}

/**
 * Show a modal if the location dist type is not supported by the establishment
 */
const doesNotSupportOnLocationOrdersModal = (): void => {
  const message = `${t(`modal.incorrect-location-distribution.message`)} ${t('modal.incorrect-location-distribution.choose-other-option')}`

  show({
    dataTestId: 'incorrect-dist-modal',
    title: t(`modal.incorrect-location-distribution.title`),
    message,
    closable: false,
    callbacks: [
      {
        label: t('terms.close'),
        action: () => {
          sessionStore.setDistribution(Distribution.TakeAway)

          close()
        },
      },
    ],
  })
}

const goToCheckout = async () => {
  tracking.send('CHECKOUT_STARTED', {
    establishment_id: establishment.value.id,
    establishment_title: establishment.value.title,
  })

  await router.push(localizedRoute({name: 'checkout'}))
}

const openEmptyCartConfirmationModal = (type = 'empty', next) => {
  show({
    closable: true,
    dataTestId: 'empty-cart-confirmation-modal',
    title: t('confirmation-modal.title'),
    message: t(`confirmation-modal.message-${type}`, {
      establishment: establishment.value.title,
    }),
    callbacks: [
      {
        label: t('confirmation-modal.cancel-btn'),
        dataTestId: 'empty-cart-confirmation-modal-cancel',
        action: () => {
          reset()
          if (typeof next === 'function') next(false)
        },
        properties: {
          type: 'secondary',
        },
      },
      {
        label: t('confirmation-modal.confirm-btn'),
        dataTestId: 'empty-cart-confirmation-modal-confirm',
        action: async () => {
          await emptyBasket()
          await reset()

          if (typeof next === 'function') next(true)
        },
      },
    ],
  })
}

function openProductNotAvailableModal(from: string, until: string) {
  show({
    closable: true,
    dataTestId: 'product-not-available-modal',
    title: t('confirmation-modal.no-timeslots.title'),
    message: t('confirmation-modal.no-timeslots.message-no-timeslots', {from, until}),
    callbacks: [
      {
        label: t('terms.ok'),
        dataTestId: 'product-not-available-ok',
        properties: {
          type: 'primary',
        },
      },
    ],
  })
}

function openLocationModal(action?: { changeDistributionTo: Distribution | undefined }) {
  state.showLocationModal = true

  if (action?.changeDistributionTo !== undefined) {
    state.changeDistributionTo = action?.changeDistributionTo
  }
}

async function updateAddress(location: Location) {
  state.locationModalError = null

  const distribution = state.changeDistributionTo ?? sessionStore.distribution

  if (distribution === Distribution.Delivery) {
    const distributionInfo = await establishmentHttp.establishmentByDistributionType(
        establishment.value.slug,
        establishment.value.location.city.country.code.toLowerCase(),
        establishment.value.location.city.slug,
        distribution,
        location,
    )

    if (!distributionInfo.is_within_delivery_area) {
      state.locationModalError = t('out-of-range.message')

      return
    }

    establishmentStore.setEstablishmentDistribution(distributionInfo)
  }

  if (state.changeDistributionTo !== null) {
    sessionStore.setDistribution(state.changeDistributionTo)

    state.changeDistributionTo = null
  }

  sessionStore.setLocation(location)

  state.showLocationModal = false
}

function openRemarkModal(product: BasketProduct) {
  state.remarkProduct = product
  state.showRemarkModal = true
}

function closeRemarkModal() {
  state.showRemarkModal = false
  state.remarkProduct = null
}

async function toggleDistribution(toDistribution: Distribution) {
  const currentDistribution = sessionStore.distribution

  const location = sessionStore.location

  const distributionInfo = await establishmentHttp.establishmentByDistributionType(
      establishment.value.slug,
      establishment.value.location.city.country.code.toLowerCase(),
      establishment.value.location.city.slug,
      toDistribution,
      location,
  )

  if (toDistribution === Distribution.Delivery && !distributionInfo.is_within_delivery_area) {
    show({
      dataTestId: 'out-of-range-modal',
      title: establishment.value?.title,
      message: t('out-of-range.message'),
      closable: false,
      callbacks: [
        ...[
          establishmentSupportDistribution(Distribution.TakeAway)
              ? {
                label: t('out-of-range.btn.get-takeaway'),
                dataTestId: 'out-of-range-modal-takeaway-callback',
                properties: {
                  type: 'secondary',
                },
                action: () => {
                  sessionStore.setDistribution(Distribution.TakeAway)
                },
              }
              : {
                label: t('out-of-range.btn.back-to-overview'),
                dataTestId: 'out-of-range-modal-return-callback',
                properties: {
                  type: 'secondary',
                },
                action: () => {
                  // in this case, the establishment only supports delivery
                  router.push(localizedRoute({name: 'marketplace'}))
                },
              },
        ],
        {
          label: t('out-of-range.btn.change-address'),
          dataTestId: 'out-of-range-modal-change-address-callback',
          action: () => {
            openLocationModal({changeDistributionTo: toDistribution})
          },
        },
      ],
    })

    return
  }

  establishmentStore.setEstablishmentDistribution(distributionInfo)
  sessionStore.setDistribution(toDistribution)

  if (cartStore.hasCart && cartStore.establishment !== null && cartStore.establishment.id === establishment.value.id) {
    cartStore.setEstablishmentDistribution(distributionInfo)
    await cartStore.updateCartByDistribution(toDistribution, currentDistribution)
  }

  marketplaceStore.setCurrentPage(1)
}

// When unmounting, clear the
onBeforeUnmount(() => {
  establishmentStore.clear()
})

</script>

<template>
  <JsonLd>
    {{ structuredData }}
  </JsonLd>

  <ClientOnly>
    <FoodModal :establishment="establishment"
               :establishment-distribution="establishmentDistribution"
               @await-empty-cart-confirmation="openEmptyCartConfirmationModal"
               @open-location-modal="state.showLocationModal = true"
               @product-not-available="openProductNotAvailableModal"
               v-if="establishment !== null && establishmentDistribution !== null"/>

    <InfoModal :establishment="establishment"
               :establishment-distribution="establishmentDistribution"
               v-if="establishment !== null && establishmentDistribution !== null"/>

    <AddReviewModal :establishment="establishment" v-if="establishment !== null"/>

    <RemarkModal :open="state.showRemarkModal"
                 :product="state.remarkProduct"
                 :special-instruction-placeholder="establishment.special_instruction_placeholder"
                 @close="closeRemarkModal"
                 v-if="establishment !== null && state.remarkProduct !== null"/>

    <LocationModal v-model="state.showLocationModal"
                   closable
                   @closed="state.locationModalError = null"
                   :error="state.locationModalError"
                   @address="updateAddress"
                   v-if="establishment !== null && establishmentDistribution !== null"
    />
  </ClientOnly>

  <DefaultLayout class="background-white">
    <EstablishmentHeaderLoading v-if="establishment === null || establishmentDistribution === null"/>
    <EstablishmentHeader :establishment="establishment" :establishment-distribution="establishmentDistribution" v-else/>

    <EstablishmentToolbarTop :search="search"
                             :establishment="establishment"
                             :establishment-distribution="establishmentDistribution"
                             @show-location-modal="openLocationModal"
                             @update-search="updateSearch"
                             @toggle-distribution="toggleDistribution"
                             v-if="distribution"/>

    <EstablishmentDiscountsLoading v-if="establishmentDiscounts === null"/>
    <EstablishmentDiscounts :discounts="establishmentDiscounts" v-else/>

    <Container>

      <div class="establishment">
        <div class="establishment__contents establishment__padded">
          <div class="establishment__offering"
               data-test-id="establishment-menu">
            <Warning pointer-cursor
                     data-test-id="no-location-but-should"
                     class="establishment__no-location-but-should"
                     @click="state.showLocationModal = true"
                     v-if="noLocationButShould">
              <b>{{ t('establishment.offering.warning') }}:</b>
              {{ t('establishment.offering.message') }}
            </Warning>

            <template v-if="establishment !== null && establishmentDistribution !== null">
              <div v-if="filteredProducts.length === 0" class="establishment__no-products">
                <Warning data-test-id="no-products">
                  {{ t('establishment.offering.no-products') }}
                </Warning>
              </div>
              <EstablishmentCategory v-for="productSet in filteredProducts"
                                     :key="productSet.id"
                                     :product-set="productSet"/>
            </template>
            <template v-else>
              <LoadingEstablishmentCategory/>
              <LoadingEstablishmentCategory/>
            </template>
          </div>
        </div>

        <div class="sidebar">
          <div class="sidebar__sticky">
            <ClientOnly>
              <!-- TODO: replace with a simple loading state -->
              <CartDesktop data-test-id="establishment-cart"
                           v-if="establishment !== null"
                           class="sidebar__cart"
                           no-tip
                           :establishment="establishment"
                           @confirm="goToCheckout"
                           @open-instructions-modal="openRemarkModal"/>
            </ClientOnly>
          </div>
        </div>
      </div>

      <EstablishmentFAQ :establishment="establishment"
                        :establishment-distribution="establishmentDistribution"
                        :faqs="establishmentFaqs"
                        class="content mb-10"
                        v-if="establishmentFaqs !== null"/>

      <!-- eslint-disable vue/no-v-html -->
      <div v-if="seoContent"
           class="pb-10 content"
           v-html="seoContent"
      />
      <!-- eslint-enable vue/no-v-html -->
    </Container>

    <ClientOnly>
      <Toolbar v-if="hasItemsFromEstablishment" class="establishment__toolbar">
        <template #top>
          <FreeDeliveryNoticeDrawer v-if="cartStore.hasBasketInfo"/>
        </template>
        <div class="toolbar__price">
          <Icon name="shopping-basket-line"/>
          {{ totalPayable }}
        </div>
        <Button data-test-id="toolbar-checkout-button" class="checkout-btn" @click="goToCheckout">
          {{ t('checkout-button.to-checkout') }}
        </Button>
      </Toolbar>
    </ClientOnly>
  </DefaultLayout>
</template>

<style lang="scss" scoped>
@import "@/assets/css/mixins/breakpoints-up.scss";
@import "@/assets/css/mixins/breakpoints-down.scss";

.checkout-btn.button {
  @include lg-down {
    width: 100%;
  }
}

.establishment-seo-information {
  display: none;
}

.establishment {
  display: flex;

  &__contents {
    display:        flex;
    flex-direction: column;
    padding:        0 1.5rem 2rem;
    width:          100%;

    @include lg-up {
      padding:      0 0 2rem 0;
      margin-right: 2rem;
      width:        calc(100% - 25rem - 2rem);
    }

    @include xl-up {
      margin-right: 4rem;
      width:        calc(100% - 25rem - 4rem);
    }

    @include xl-up {
      margin-right: 4rem;
      width:        calc(100% - 25rem - 4rem);
    }
  }

  &__no-products {
    margin-top: 2rem;
  }

  &__sidebar {
    margin-left: auto;
  }

  &__offering {
    order:      3;
    margin-top: -0.6rem;

    @include lg-up {
      margin-top: 1rem;
    }
  }

  &__no-location-but-should {
    margin-top: 1rem;
  }

  &__toolbar {
    &:deep(.toolbar__content) {
      gap:             1.5rem;
      // padding: 1.125rem 1.5rem calc(1.125rem + env(safe-area-inset-bottom)) 2rem;
      padding:         0 1.5rem 0 2rem;
      margin-bottom:   env(safe-area-inset-bottom);
      height:          56px;
      justify-content: center;
      align-items:     center;

      @include md-up {
        height: 88px;
      }
    }
  }
}

.content {
  margin-right: 1.5rem;
  margin-left:  1.5rem;

  @include lg-up {
    margin-right: 0;
    margin-left:  0;
  }
}

.establishment-discounts {
  margin-top: 2rem;
}

// TODO: remove after redesign
.background-white {
  background-color: #ffffff;
}

.toolbar {
  &__price {
    display:     flex;
    width:       max-content;
    font-weight: 700;
    align-items: center;
    gap:         0.5rem;
  }
}

.sidebar {
  display: none;
  width:   25rem;

  @include lg-up {
    display: block;
  }

  &__sticky {
    max-height: 680px;
    position:   sticky;
    top:        6rem;
  }

  &__cart {
    margin: 4rem 0;
  }
}
</style>
